<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $props.projectSupplier ? $t('ProjectSettings.VendorsTab.ProjectSupplierModal.header.updateVendor') : $t('ProjectSettings.VendorsTab.ProjectSupplierModal.header.addVendor') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="overflow-y-auto h-full w-full">
        <form
          action=""
          class="space-y-4"
          @submit.prevent="saveOrder"
        >
          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.vendorName') }}
            </p>

            <AppTypeAhead
              v-model="supplierForm.form.supplier_name"
              name="supplier_name"
              :suggestion-callback="getSuggestions"
              :initial-suggestions="getSuggestions"
              data-test="supplier-name"
              :error="supplierForm.getErrors('supplier_id') || supplierForm.getErrors('supplier_name')"
              :disabled="$props.projectSupplier"
              class="disabled:bg-gray-300 form-input"
              @value-selected="selectSupplier"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.type') }}
            </p>

            <AppSelect
              v-model="supplierForm.form.type"
              name="type"
              class="capitalize form-input pr-8"
              :error="supplierForm.getErrors('type')"
              :options="supplierTypes"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.contactEmail') }}
            </p>

            <AppInput
              v-model="supplierForm.form.contact_email"
              name="contact_email"
              type="text"
              :error="supplierForm.getErrors('contact_email')"
              data-test="contact_email"
              class="form-input"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.contactName') }}
            </p>

            <AppInput
              v-model="supplierForm.form.contact_name"
              name="contact_name"
              type="text"
              :error="supplierForm.getErrors('contact_name')"
              data-test="contact_name"
              class="form-input"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.address') }}
            </p>

            <AppInput
              v-model="supplierForm.form.address"
              name="address"
              type="text"
              :error="supplierForm.getErrors('address')"
              data-test="address"
              class="form-input"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.contactPhone') }}
            </p>

            <AppInput
              v-model="supplierForm.form.contact_number"
              name="contact_number"
              type="text"
              :error="supplierForm.getErrors('contact_number')"
              data-test="contact_number"
              class="form-input"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.saturdayMultiplier') }}
            </p>

            <AppInput
              v-model.number="supplierForm.form.saturday_multiplier"
              name="saturday_multiplier"
              type="text"
              :error="supplierForm.getErrors('saturday_multiplier')"
              data-test="saturday_multiplier"
              class="form-input"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.sundayMultiplier') }}
            </p>

            <AppInput
              v-model.number="supplierForm.form.sunday_multiplier"
              name="sunday_multiplier"
              type="text"
              :error="supplierForm.getErrors('sunday_multiplier')"
              data-test="sunday_multiplier"
              class="form-input"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.publicHolidayMultiplier') }}
            </p>

            <AppInput
              v-model.number="supplierForm.form.public_holiday_multiplier"
              name="public_holiday_multiplier"
              type="text"
              :error="supplierForm.getErrors('public_holiday_multiplier')"
              data-test="public_holiday_multiplier"
              class="form-input"
            />
          </label>

          <label class="flex items-center justify-between space-x-2">
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.contractValue') }}
            </p>

            <AppCurrencyInput
              v-model.number="supplierForm.form.contract_value"
              name="contract_value"
              type="text"
              :error="supplierForm.getErrors('contract_value')"
              data-test="contract_value"
              class="form-input"
            />
          </label>

          <div class="flex flex-col items-start space-y-4">
            <div>
              <legend>
                {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.overtimeThresholds') }}
              </legend>

              <div class="flex items-end space-x-4">
                <label>
                  {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.hourThreshold') }}

                  <AppInput
                    v-model.number="supplierForm.form.threshold"
                    name="activity"
                    type="text"
                    :error="supplierForm.getErrors('threshold')"
                    class="form-input"
                    @keydown.enter.prevent="addThreshold"
                  />
                </label>

                <label>
                  {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.multiplier') }}

                  <AppInput
                    v-model.number="supplierForm.form.multiplier"
                    name="activity"
                    type="text"
                    class="form-input"
                    @keydown.enter.prevent="addThreshold"
                  />
                </label>

                <button
                  type="button"
                  class="btn btn--secondary-blue h-min"
                  @click.prevent="addThreshold"
                >
                  {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.addThreshold') }}
                </button>
              </div>
            </div>

            <ul class="flex flex-col space-y-2">
              <li
                v-for="(overtime, i) in supplierForm.form.overtime"
                :key="i"
                class="w-full flex card__list-item"
              >
                <p>
                  {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.label.aboveHoursRate', { threshold: overtime.threshold, multiplier: overtime.multiplier }) }}
                </p>

                <button type="button">
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class=" w-6 h-6"
                    @click="removeThreshold(i)">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                </button>
              </li>
            </ul>
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          v-if="projectsStore.project?.unarchived && $props.projectSupplier"
          type="button"
          class="btn btn--tertiary-blue"
          @click="deleteSupplier"
        >
          {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.button.delete') }}
        </button>

        <div class="space-x-2 ml-auto">
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click="$emit('close'); supplierForm.resetData()"
          >
            {{ $t('ProjectSettings.VendorsTab.ProjectSupplierModal.button.cancel') }}
          </button>

          <button
            v-if="projectsStore.project?.unarchived"
            type="button"
            class="btn btn--primary-blue"
            data-test="save-order"
            @click="saveOrder"
          >
            {{ $props.projectSupplier ? $t('ProjectSettings.VendorsTab.ProjectSupplierModal.button.update') : $t('ProjectSettings.VendorsTab.ProjectSupplierModal.button.create') }}
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import ProjectSupplier, { SupplierType } from '@/models/ProjectSupplier';
import { useProjectsStore } from '@/store/projects';
import { useProjectSupplierStore } from '@/store/projectSupplier';
import { useSuppliersStore } from '@/store/supplier';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  projectSupplier?: ProjectSupplier;
  projectId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const projectSuppliersStore = useProjectSupplierStore();

const supplierForm = useFormData({
  supplier_name: '',
  threshold: 0,
  multiplier: 0,
  overtime: [] as ({ threshold: number; multiplier: number })[],
  saturday_multiplier: 1,
  sunday_multiplier: 1,
  public_holiday_multiplier: 1,
  supplier_id: undefined,
  type: 0,
  contact_email: '',
  address: '',
  contact_number: '',
  contact_name: '',
  contract_value: undefined,
});

watch(() => props.projectSupplier, (newValue) => {
  if(newValue) {
    supplierForm.form.supplier_name = newValue.supplier.name;

    // supplierForm.form.overtime = props.projectSupplier.overtime || [];
    // supplierForm.form.saturday_multiplier = props.projectSupplier.saturday_multiplier;
    // supplierForm.form.sunday_multiplier = props.projectSupplier.sunday_multiplier;
    // supplierForm.form.public_holiday_multiplier = props.projectSupplier.public_holiday_multiplier;
    supplierForm.setData(newValue, [
      'supplier_name',
      'supplier_id',
      'multiplier',
      'threshold',
    ]);

    // supplierForm.setData(newValue.subcontractor_details, ['contract_start_date']);
  }
}, { immediate: true });

const updateSupplierAction = useStoreApiAction(projectSuppliersStore.updateProjectSupplier);
const createSupplierAction = useStoreApiAction(projectSuppliersStore.createProjectSupplier);

const saveOrder = () => {
  supplierForm.resetErrors();

  if(props.projectSupplier) {
    updateSupplierAction.request(props.projectSupplier.id, supplierForm.form).then(() => {
      toast.success(t('ProjectSettings.VendorsTab.ProjectSupplierModal.messages.vendorUpdated'));
      emits('close');
      supplierForm.resetData();
    }).catch((error) => {
      supplierForm.setErrors(error.data);
    });
  } else {
    createSupplierAction.request(props.projectId, supplierForm.form).then(() => {
      toast.success(t('ProjectSettings.VendorsTab.ProjectSupplierModal.messages.vendorCreated'));
      emits('close');
      supplierForm.resetData();
    }).catch((error) => {
      supplierForm.setErrors(error.data);
    });
  }
};

const deleteSupplier = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.VendorsTab.ProjectSupplierModal.messages.removeVendorTitle'),
    text: t('ProjectSettings.VendorsTab.ProjectSupplierModal.messages.removeVendorText'),
    showConfirmButton: true,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      projectSuppliersStore.deleteProjectSupplier(props.projectSupplier.id).then(() => {
        emits('close');
      });
    }
  });
};

const addThreshold = () => {
  if(supplierForm.form.multiplier && supplierForm.form.threshold) {
    supplierForm.form.overtime.push({
      multiplier: supplierForm.form.multiplier,
      threshold: supplierForm.form.threshold,
    });

    supplierForm.form.multiplier = undefined;
    supplierForm.form.threshold = undefined;
  }
};

const removeThreshold = (index: number) => {
  supplierForm.form.overtime.splice(index, 1);
};

const suppliersStore = useSuppliersStore();
const suggestedSuppliers = ref<{ id: number; name: string }[]>([]);

const getSuggestions = async () => {
  suggestedSuppliers.value = await suppliersStore
    .search({ name: supplierForm.form.supplier_name, project_id: parseInt(`${props.projectId}`) });

  return suggestedSuppliers.value.map((value) => value.name);
};

const selectSupplier = (index: number) => {
  nextTick(() => {
    supplierForm.form.supplier_id = suggestedSuppliers.value[index].id;
  });
};

watch(() => supplierForm.form.supplier_name, (newValue, oldValue) => {
  if(
    newValue !== oldValue &&
    supplierForm.form.supplier_id !== undefined
  ) {
    supplierForm.form.supplier_id = undefined;
  }
});

const supplierTypes = computed(() => {
  const types = [];

  for(const type in SupplierType) {
    const value = SupplierType[type];

    types.push({ value, label: type.replace('_', ' ') });
  }

  return types;
});

const projectsStore = useProjectsStore();
</script>

<style scoped></style>
